interface ScreeningCalendarProps {
  year: number;
  month: number;
  availableDates: string[];
  selected: string;
  onSelect: (date: string) => void;
  onMonthChange: (delta: number) => void;
}

const weekdays = ["日", "一", "二", "三", "四", "五", "六"];

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`;
}

export default function ScreeningCalendar({
  year,
  month,
  availableDates,
  selected,
  onSelect,
  onMonthChange,
}: ScreeningCalendarProps) {
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const availableSet = new Set(availableDates);

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const todayStr = "2026-10-13";

  return (
    <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => onMonthChange(-1)}
          aria-label="上一个月"
          className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <h3 className="font-heading font-semibold text-foreground-950">
          {year} 年 {month + 1} 月
        </h3>
        <button
          type="button"
          onClick={() => onMonthChange(1)}
          aria-label="下一个月"
          className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-200/60 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekdays.map((w) => (
          <div key={w} className="text-center text-xs text-foreground-500 font-label py-1">
            {w}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (day === null) return <div key={`empty-${i}`} />;
          const dateStr = `${year}-${pad(month + 1)}-${pad(day)}`;
          const available = availableSet.has(dateStr);
          const isSelected = selected === dateStr;
          const isToday = dateStr === todayStr;
          return (
            <button
              key={dateStr}
              type="button"
              disabled={!available}
              onClick={() => onSelect(dateStr)}
              className={`relative aspect-square flex flex-col items-center justify-center rounded-md text-sm transition-colors ${
                isSelected
                  ? "bg-accent-500 text-background-50 cursor-pointer"
                  : available
                    ? "text-foreground-900 hover:bg-background-200/70 cursor-pointer"
                    : "text-foreground-500/50 cursor-not-allowed"
              }`}
            >
              <span className="font-label">{day}</span>
              {available && !isSelected && (
                <span className="absolute bottom-1 w-1 h-1 rounded-full bg-accent-500" />
              )}
              {isToday && !isSelected && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary-500" />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-5 mt-5 pt-4 border-t border-background-200/60 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
          有放映场次
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
          今天
        </span>
      </div>
    </div>
  );
}